import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import GoogleLogoMark from '../components/GoogleLogoMark';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { mapGoogleSignInError, needsEmailVerification, useAuth } from '../context/AuthContext';
import Seo from '../seo/Seo';
import { trackEvent } from '../utils/analytics';

function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const { user, register, loginWithGoogle, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = (searchParams.get('redirect') || '').trim();
  const redirectTarget = redirectParam.startsWith('/') ? redirectParam : '';
  const loginPath = redirectTarget
    ? `/login?redirect=${encodeURIComponent(redirectTarget)}`
    : '/login';
  const verifiedDestination = redirectTarget || '/ai-tools/ai-room-decoration';

  if (!loading && isAuthenticated && needsEmailVerification(user)) {
    return <Navigate to="/verify-email" state={{ from: { pathname: verifiedDestination } }} replace />;
  }

  if (!loading && isAuthenticated && !needsEmailVerification(user)) {
    return <Navigate to={verifiedDestination} replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!email) {
      setError('Email is required.');
      return;
    }
    if (!password) {
      setError('Password is required.');
      return;
    }
    if (!confirmPassword) {
      setError('Confirm password is required.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setError('');
      await register({ email, password });
      trackEvent('sign_up', { method: 'email' });
      setPassword('');
      setConfirmPassword('');
      navigate('/verify-email', { replace: true, state: { from: { pathname: verifiedDestination } } });
    } catch (firebaseError) {
      if (firebaseError.code === 'auth/email-already-in-use') {
        setError('This email is already used.');
      } else if (
        firebaseError.code === 'auth/unauthorized-continue-uri' ||
        firebaseError.code === 'auth/invalid-continue-uri'
      ) {
        setError(
          'Email verification failed: this domain is not allowed for Firebase sign-in redirects. Ask the administrator to add it under Firebase Console → Authentication → Settings → Authorized domains.'
        );
      } else {
        setError('Unable to register. Please try again.');
      }
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setGoogleLoading(true);
      const { isNewUser } = await loginWithGoogle();
      if (isNewUser) {
        trackEvent('sign_up', { method: 'google' });
      }
      navigate(verifiedDestination, { replace: true });
    } catch (firebaseError) {
      setError(mapGoogleSignInError(firebaseError));
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="page landing-bg auth-sheet">
      <Seo
        title="Create Account | MyRoomsAI"
        description="Create your MyRoomsAI account and start generating AI room design ideas from your own room photos."
        canonicalPath="/register"
        noindex
      />
      <Navbar />
      <main className="auth-page">
        <div className="auth-card card">
          <h1>Create account</h1>
          <p>Start redesigning rooms with AI in just a few steps.</p>

          <form onSubmit={handleSubmit} className="auth-form">
            <label htmlFor="register-email">Email</label>
            <input
              id="register-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />

            <label htmlFor="register-password">Password</label>
            <input
              id="register-password"
              type="password"
              placeholder="Create a strong password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />

            <label htmlFor="register-confirm-password">Confirm Password</label>
            <input
              id="register-confirm-password"
              type="password"
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
            />

            <button type="submit" className="btn btn-primary full-width">
              Register
            </button>
            <p className="auth-or">or</p>
            <button
              type="button"
              className="btn btn-ghost full-width btn-google-signin"
              onClick={handleGoogleSignIn}
              disabled={googleLoading}
            >
              {googleLoading ? null : <GoogleLogoMark size={20} />}
              {googleLoading ? 'Signing in…' : 'Continue with Google'}
            </button>
            <button
              type="button"
              className="btn btn-ghost full-width"
              onClick={() => navigate(loginPath)}
            >
              Login
            </button>
            {error ? (
              <p className="auth-error" role="alert">
                {error}
              </p>
            ) : null}
          </form>

          <p className="switch-auth">
            Already have an account? <Link to={loginPath}>Sign in</Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default RegisterPage;
